/**
 * NotificationToast — FCM 수신 시 화면 상단 슬라이드 토스트
 * 3초 후 자동 숨김, 클릭 시 닫기
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useNotification, type AppNotification } from "../../contexts/NotificationContext";
import { getNotifAvatar, getNotifBoardKey, fmtNotifTime, routeNotificationClick } from "../../utils/notificationCard";

export default function NotificationToast() {
  const { t } = useTranslation();
  const { notifications, markAsRead } = useNotification();
  const [current, setCurrent] = useState<AppNotification | null>(null);
  const [show, setShow] = useState(false);
  const lastIdRef = useRef("");

  // 1) 새 알림 감지
  useEffect(() => {
    if (notifications.length === 0) return;
    const newest = notifications[0];
    if (newest.id === lastIdRef.current) return;
    lastIdRef.current = newest.id;
    setCurrent(newest);
    setShow(true);
  }, [notifications]);

  // 2) 3초 후 자동 숨김
  useEffect(() => {
    if (!show) return;
    const timer = window.setTimeout(() => setShow(false), 3000);
    return () => window.clearTimeout(timer);
  }, [show]);

  const handleClick = useCallback(() => {
    if (!current) return;
    setShow(false);
    markAsRead(current.id);
    routeNotificationClick(current);
  }, [current, markAsRead]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShow(false);
  }, []);

  if (!current) return null;

  // 종 드롭다운 카드와 동일한 레이아웃(아바타 TPA/TP + 게시판명 · 닉네임 + 게시글제목/댓글 + 시각)
  const { avatarLabel, avatarBg } = getNotifAvatar(current);
  const boardKey = getNotifBoardKey(current);
  const boardName = boardKey ? t(`notif.board_${boardKey}`, { defaultValue: boardKey }) : "";

  return (
    <div
      onClick={handleClick}
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-sm cursor-pointer
        rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-black/10
        px-4 py-3 flex items-start gap-2.5
        transition-all duration-300 ease-out
        ${show ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"}`}
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-black mt-0.5 ${avatarBg}`}>
        {avatarLabel}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {boardName && <span className="text-[10px] font-bold text-sky-600 shrink-0">{boardName}</span>}
          {current.nickName && <span className="text-[10px] text-slate-500 truncate">· {current.nickName}</span>}
        </div>
        <div className="text-[11px] text-slate-700 truncate">{current.postTitle || current.body}</div>
      </div>
      <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">{fmtNotifTime(current.timestamp)}</span>
      <button onClick={handleClose} className="shrink-0 text-slate-400 hover:text-slate-600 mt-0.5">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
